import writeXlsxFile from 'write-excel-file';
import dayjs from '@/utils/dayjs';
import { excelStyle } from '@/helpers/xlsxStyles';
import { InvoiceV2 } from '@/store/invoice/useGetInvoicesV2';
import { Charge } from '@/store/invoice/useGetCharge';
import { isExpiredInvoice, STATUS_CONFIG } from './const';

const { fontSize } = excelStyle;

const cellStyle = {
    ...excelStyle.cellStyle,
    align: 'center' as const,
    alignVertical: 'center' as const,
};

const formatDate = (date?: string): string =>
    date ? dayjs(date).format('DD/MM/YYYY') : '';

const centsToReais = (cents: number): number => cents / 100;

const SHOPMANAGER_COLUMN_NAMES = ['ID Lojista', 'E-mail Lojista'];

const withoutShopmanagerColumns = <Column extends { column: string }>(
    schema: Column[],
): Column[] =>
    schema.filter(({ column }) => !SHOPMANAGER_COLUMN_NAMES.includes(column));

const schemaInvoices = [
    {
        column: 'ID Lojista',
        value: (row: InvoiceV2): number | undefined => row.shopmanager?.id,
        ...cellStyle,
        width: 15,
    },
    {
        column: 'CPF/CNPJ',
        value: (row: InvoiceV2): string => row.doc,
        ...cellStyle,
    },
    {
        column: 'E-mail Lojista',
        value: (row: InvoiceV2): string => row.shopmanager?.user?.email ?? '',
        ...cellStyle,
        width: 25,
    },
    {
        column: 'Data de emissão',
        value: (row: InvoiceV2): string => formatDate(row.issuedAt),
        ...cellStyle,
    },
    {
        column: 'Data de vencimento',
        value: (row: InvoiceV2): string => formatDate(row.dueDate),
        ...cellStyle,
    },
    {
        column: 'Valor',
        value: (row: InvoiceV2): number => centsToReais(row.value),
        type: Number,
        format: '#,##0.00',
        ...cellStyle,
    },
    {
        column: 'Status',
        value: (row: InvoiceV2): string =>
            STATUS_CONFIG[row.status]?.label ?? row.status,
        ...cellStyle,
    },
    {
        column: 'Código de barras',
        value: (row: InvoiceV2): string =>
            isExpiredInvoice(row) ? 'Boleto expirado' : row.barcode ?? '',
        ...cellStyle,
        width: 30,
    },
];

const schemaCharges = [
    {
        column: 'ID Lojista',
        value: (row: Charge): number => row.store?.shopmanager_id,
        ...cellStyle,
        width: 15,
    },
    {
        column: 'E-mail Lojista',
        value: (row: Charge): string => row.store?.shopManager?.user?.email,
        ...cellStyle,
        width: 25,
    },
    {
        column: 'Loja',
        value: (row: Charge): string => row.store?.name,
        ...cellStyle,
    },
    {
        column: 'Mês',
        value: (row: Charge): string =>
            row.reference_date ? dayjs(row.reference_date).format('MMMM') : '-',
        ...cellStyle,
    },
    {
        column: 'Valor cobrado automaticamente',
        value: (row: Charge): number => centsToReais(row.automatic_monthly_fee),
        type: Number,
        format: '#,##0.00',
        ...cellStyle,
    },
    {
        column: 'Valor a ser pago via boleto',
        value: (row: Charge): number => centsToReais(row.monthly_invoice),
        type: Number,
        format: '#,##0.00',
        ...cellStyle,
    },
];

const exportOptions = {
    headerStyle: {
        ...excelStyle.headerStyle,
        align: 'center' as const,
        alignVertical: 'center' as const,
        fontWeight: 'bold' as const,
    },
    fontSize,
};

const fileDate = () => dayjs().format('DD-MM-YYYY');

export const exportInvoices = (data: InvoiceV2[], isAdmin: boolean) =>
    writeXlsxFile(data, {
        schema: isAdmin
            ? schemaInvoices
            : withoutShopmanagerColumns(schemaInvoices),
        ...exportOptions,
        fileName: `boletos_${fileDate()}.xlsx`,
    });

export const exportCharges = (data: Charge[], isAdmin: boolean) =>
    writeXlsxFile(data, {
        schema: isAdmin
            ? schemaCharges
            : withoutShopmanagerColumns(schemaCharges),
        ...exportOptions,
        fileName: `cobrancas_${fileDate()}.xlsx`,
    });
